import { change, InjectedFormProps, reduxForm } from "redux-form";
import { isEmpty } from "lodash";
import { useState } from "react";

import { Address, SchedulerWizardFormValues } from "src/interfaces";
import { Button, Loader, Separator, TextDisplay, Wrapper } from "src/styled";
import {
  hasCity,
  hasCoordinates,
  hasCountry,
  hasStreet,
  hasStreetNumber,
  hasZip,
  isRequired,
} from "src/utils/validators";
import { loadPickupsInZone, validateAddress } from "src/redux/reducers";
import { LocationPickerGoogleMaps, TypedField } from "src/components";
import { translate } from "src/services";
import { useAppDispatch, useAppSelector } from "src/redux/store";

export const WIZARD_FORM_NAME = "wizardForm";

interface PropsWithoutReduxForm {}

type Props = PropsWithoutReduxForm &
  InjectedFormProps<SchedulerWizardFormValues, PropsWithoutReduxForm>;

const AddressForm = ({ handleSubmit }: Props) => {
  const [isAddressFilled, setIsAddressFilled] = useState<boolean>(false);
  const [isStreetNumberInvalid, setIsStreetNumberInvalid] = useState<boolean>(true);
  const [isZipInvalid, setIsZipInvalid] = useState<boolean>(true);

  const cityCustomErrorValidationMessage =
    useAppSelector((state) => state.citySettings.settings?.vendorSettings.validationErrorMessage) ||
    translate("alertMessages.addressValidationError");

  const { isLoading, isLoadingPickupsInZone } = useAppSelector(
    (state) => state.bulkyItemsScheduler
  );

  const hasAddressValidationError = useAppSelector(
    (state) => state.bulkyItemsScheduler.errors.addressValidation
  );
  const addressValidationResult = useAppSelector(
    (state) => state.bulkyItemsScheduler.addressValidation
  );

  const dispatch = useAppDispatch();

  const onAddressChange = async (_: any, address: Address) => {
    const newAddress = {
      ...address,
      line1: address.formattedAddress,
    };

    const isAddressFilled = true;
    const isStreetNumberValid = !!newAddress.streetNumber && newAddress.streetNumber !== "";
    const isZipValid = !!newAddress.zip && newAddress.zip !== "";

    setIsAddressFilled(isAddressFilled);
    setIsStreetNumberInvalid(!isStreetNumberValid);
    setIsZipInvalid(!isZipValid);

    if (isAddressFilled && isStreetNumberValid && isZipValid) {
      await validateAddress(newAddress)(dispatch).then((result) => {
        if (result?.addressExists) {
          dispatch(change(WIZARD_FORM_NAME, "address", newAddress));
        }

        loadPickupsInZone(
          result?.zoneIds[0] || 0,
          !!result?.locationId ? result?.locationId : undefined
        )(dispatch);
      });
    }
  };

  const isDisabled =
    hasAddressValidationError ||
    isLoading ||
    isLoadingPickupsInZone ||
    isEmpty(addressValidationResult);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Wrapper padding="0 50px" maxWidth={500} margin="no auto">
        <Separator height={100} />
        <TextDisplay margin="no no medium" block>
          <b>{translate("wizardPage.addressLabel")}</b>
        </TextDisplay>

        <TypedField
          component={LocationPickerGoogleMaps}
          name="address"
          onChange={onAddressChange}
          props={{
            fullWidth: true,
            margin: "no auto",
            placeholder: translate("common.yourAddress"),
            label: translate("wizardPage.addressLabel"),
          }}
          validate={[
            isRequired,
            hasCoordinates,
            hasCountry,
            hasCity,
            hasStreet,
            hasStreetNumber,
            hasZip,
          ]}
        />

        {isLoading && <Loader />}

        {hasAddressValidationError && !isLoading && !isLoadingPickupsInZone && (
          <TextDisplay margin="small no medium" block color="alert">
            <em>{cityCustomErrorValidationMessage}</em>
          </TextDisplay>
        )}

        {isAddressFilled && (
          <>
            {isStreetNumberInvalid && isZipInvalid && (
              <TextDisplay margin="small no medium" block color="alert">
                <em>{translate("alertMessages.addressValidationStreetNumberOrZip")}</em>
              </TextDisplay>
            )}

            {isStreetNumberInvalid && !isZipInvalid && (
              <TextDisplay margin="small no medium" block color="alert">
                <em>{translate("alertMessages.addressValidationStreetNumber")}</em>
              </TextDisplay>
            )}

            {!isStreetNumberInvalid && isZipInvalid && (
              <TextDisplay margin="small no medium" block color="alert">
                <em>{translate("alertMessages.addressValidationZip")}</em>
              </TextDisplay>
            )}
          </>
        )}

        <Separator height={50} />

        <Wrapper display="flex" justifyContent="center">
          <Button
            type="submit"
            color="primary"
            size="medium"
            borderRadius="100px"
            disabled={isDisabled || (isAddressFilled && (isStreetNumberInvalid || isZipInvalid))}
            loadingIndicator={
              isAddressFilled &&
              !isStreetNumberInvalid &&
              !isZipInvalid &&
              (isLoading || isLoadingPickupsInZone || isEmpty(addressValidationResult)) &&
              !hasAddressValidationError
            }
          >
            {translate("common.letsGetStarted")}
          </Button>
        </Wrapper>

        <Separator height={100} />
      </Wrapper>
    </form>
  );
};

export default reduxForm<SchedulerWizardFormValues, PropsWithoutReduxForm>({
  form: WIZARD_FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(AddressForm);
